import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';

import MainCard from 'components/MainCard';
import apiClient from 'utils/apiClient';
import useGet from 'hooks/useGet';

const paymentStatuses = [
  'created',
  'pending',
  'authorized',
  'captured',
  'failed',
  'cancelled',
  'refunded'
];

const orderStatuses = ['pending', 'placed', 'processing', 'packed', 'readytoship', 'shipped', 'delivered'];

const toDisplayText = (value) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  return String(value);
};

const normalizeText = (value) => {
  if (value === undefined || value === null) return '';
  const text = String(value).trim();
  return text;
};

const pickDefined = (...values) => {
  for (const value of values) {
    if (value === 0) return value;
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const normalizeList = (value) => {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) return value.flatMap(normalizeList);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        return normalizeList(parsed);
      } catch {
        return [trimmed];
      }
    }
    return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [String(value)];
};

const normalizePurity = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const upper = raw.toUpperCase().replace(/\s+/g, '');
  if (/\d+K/.test(upper)) return upper;
  const digits = upper.replace(/[^\d]/g, '');
  if (digits) return `${digits}K`;
  return upper;
};

const normalizeMetalTypes = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(normalizeMetalTypes);
  if (typeof value === 'string') {
    return value
      .split(/[\/,|]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const sumWeightValues = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  if (Array.isArray(value)) {
    return value.reduce((sum, item) => sum + sumWeightValues(item), 0);
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const weightFromItem = (item) => {
  if (!item || typeof item !== 'object') return 0;
  return sumWeightValues(
    item.weight ?? item.weights ?? item.carat ?? item.caratWeight ?? item.value
  );
};

const sumWeights = (items) => {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => sum + weightFromItem(item), 0);
};

const collectGemstoneItems = (gemstones) => {
  if (!gemstones) return [];
  if (Array.isArray(gemstones)) return gemstones;
  const precious = gemstones.preciousGemstones || {};
  const semi = gemstones.semiPreciousGemstones || {};
  const preciousItems = Object.values(precious).flatMap((list) => (Array.isArray(list) ? list : []));
  const semiItems = Object.values(semi).flatMap((list) => (Array.isArray(list) ? list : []));
  return [...preciousItems, ...semiItems];
};

const formatWeight = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toFixed(2);
};

const buildAttributesLabel = (attrs, excludeKeys = []) => {
  if (!attrs || typeof attrs !== 'object') return '-';
  const entries = Object.entries(attrs).filter(
    ([key, value]) =>
      !excludeKeys.includes(key) && value !== undefined && value !== null && value !== ''
  );
  if (!entries.length) return '-';
  const labelMap = {
    color: 'Color',
    diamondQuality: 'Diamond Quality',
    goldPurity: 'Gold Purity',
    size: 'Size',
    metalType: 'Metal Type'
  };
  return entries
    .map(([key, value]) => {
      const label =
        labelMap[key] ||
        key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (char) => char.toUpperCase());
      return `${label}: ${value}`;
    })
    .join(' | ');
};

const getGoldWeightByPurity = (entity = {}, purity) => {
  const normalized = normalizePurity(purity);
  if (!normalized) return null;
  const lowerKey = normalized.toLowerCase();
  const lookup = {
    '9K': pickDefined(
      entity?.gold_9k_weight,
      entity?.gold9kWeight,
      entity?.gold9KWeight,
      entity?.[`gold_${lowerKey}_weight`],
      entity?.[`gold${normalized}Weight`],
      entity?.[`gold${lowerKey}Weight`]
    ),
    '14K': pickDefined(
      entity?.gold_14k_weight,
      entity?.gold14kWeight,
      entity?.gold14KWeight,
      entity?.[`gold_${lowerKey}_weight`],
      entity?.[`gold${normalized}Weight`],
      entity?.[`gold${lowerKey}Weight`]
    ),
    '18K': pickDefined(
      entity?.gold_18k_weight,
      entity?.gold18kWeight,
      entity?.gold18KWeight,
      entity?.[`gold_${lowerKey}_weight`],
      entity?.[`gold${normalized}Weight`],
      entity?.[`gold${lowerKey}Weight`]
    ),
    '22K': pickDefined(
      entity?.gold_22k_weight,
      entity?.gold22kWeight,
      entity?.gold22KWeight,
      entity?.[`gold_${lowerKey}_weight`],
      entity?.[`gold${normalized}Weight`],
      entity?.[`gold${lowerKey}Weight`]
    )
  };

  const direct =
    lookup[normalized] ??
    pickDefined(
      entity?.goldWeights?.[normalized],
      entity?.goldWeights?.[lowerKey],
      entity?.gold_weights?.[normalized],
      entity?.gold_weights?.[lowerKey],
      entity?.metal?.gold?.weights?.[normalized],
      entity?.metal?.gold?.weights?.[lowerKey]
    );

  return direct ?? null;
};

const getGoldWeight = ({ variant, product, purity }) => {
  const byPurity = pickDefined(
    getGoldWeightByPurity(variant, purity),
    getGoldWeightByPurity(product, purity)
  );
  const direct = pickDefined(
    variant?.goldWeight,
    variant?.gold_weight,
    variant?.metal?.gold?.weight,
    variant?.metal?.gold?.calculatedGoldWeight,
    variant?.metal?.gold?.baseGoldWeight,
    product?.goldWeight,
    product?.gold_weight,
    product?.gold_net_weight,
    product?.metal?.gold?.calculatedGoldWeight,
    product?.metal?.gold?.baseGoldWeight
  );
  return pickDefined(byPurity, direct);
};

const getPlatinumWeight = ({ variant, product, item }) =>
  pickDefined(
    variant?.platinumWeight,
    variant?.platinum_weight,
    variant?.metal?.platinum?.weight,
    item?.platinumWeight,
    item?.platinum_weight,
    product?.platinumWeight,
    product?.platinum_weight,
    product?.metal?.platinum?.weight
  );

const pickArray = (...values) => values.find((value) => Array.isArray(value) && value.length);

const getDiamondWeight = ({ variant, product, item }) => {
  const explicit = pickDefined(
    variant?.diamondWeight,
    variant?.diamond_weight,
    item?.diamondWeight,
    item?.diamond_weight,
    product?.diamondWeight,
    product?.diamond_weight
  );
  if (explicit !== null) return explicit;
  const diamondItems = pickArray(variant?.diamond, item?.diamond, product?.diamond) || [];
  return sumWeights(diamondItems);
};

const getGemstoneWeight = ({ variant, product, item }) => {
  const explicit = pickDefined(
    variant?.gemstoneWeight,
    variant?.gemstone_weight,
    item?.gemstoneWeight,
    item?.gemstone_weight,
    product?.gemstoneWeight,
    product?.gemstone_weight
  );
  if (explicit !== null) return explicit;
  const gemstones = pickDefined(variant?.gemstones, item?.gemstones, product?.gemstones);
  return sumWeights(collectGemstoneItems(gemstones));
};

const resolveImage = (item, variant, product) =>
  pickDefined(
    variant?.media?.heroImage,
    variant?.heroImage,
    product?.media?.heroImage,
    product?.heroImage,
    product?.media?.galleryImages?.[0],
    product?.galleryImages?.[0],
    item?.image
  );

const formatAddress = (value) => {
  if (!value) return '-';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '-';
  }
  if (typeof value !== 'object') {
    return toDisplayText(value);
  }

  const parts = [
    value?.name,
    value?.line1,
    value?.line2,
    value?.street,
    value?.address,
    value?.city,
    value?.state,
    value?.postalCode,
    value?.pincode,
    value?.zip,
    value?.country
  ]
    .map(normalizeText)
    .filter(Boolean);

  if (parts.length) {
    return [...new Set(parts)].join(', ');
  }

  try {
    return JSON.stringify(value);
  } catch {
    return '-';
  }
};

const formatPersonalization = (value) => {
  if (!value) return '-';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '-';
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => formatPersonalization(item))
      .filter((item) => item && item !== '-');
    return parts.length ? parts.join(' | ') : '-';
  }
  if (typeof value !== 'object') {
    return toDisplayText(value);
  }

  const entries = Object.entries(value).filter(
    ([, entryValue]) => entryValue !== undefined && entryValue !== null && entryValue !== ''
  );

  if (!entries.length) return '-';

  return entries
    .map(([key, entryValue]) => {
      const label = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/[_-]+/g, ' ')
        .replace(/^./, (char) => char.toUpperCase());
      return `${label}: ${toDisplayText(entryValue)}`;
    })
    .join(' | ');
};

export default function PaymentOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [updating, setUpdating] = useState(false);
  const [trackingNo, setTrackingNo] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [selectedOrderStatus, setSelectedOrderStatus] = useState('');
  const [orderStatusUpdating, setOrderStatusUpdating] = useState(false);
  const [trackingUpdating, setTrackingUpdating] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const { data, error, loading, mutate } = useGet(id ? `/api/payments/admin/orders/${id}` : null);

  const order = data?.order;
  const status = useMemo(() => order?.payment?.status || '', [order]);
  const effectiveStatus = selectedStatus || status;
  const currentTrackingNo =
    order?.trackingId || order?.trackingNo || order?.metadata?.notes?.trackingNo || '';
  const currentTrackingUrl = order?.trackingUrl || order?.metadata?.notes?.trackingUrl || '';
  const currentOrderStatus = order?.orderStatus || 'pending';
  const effectiveOrderStatus = selectedOrderStatus || currentOrderStatus;
  const customerEmail = pickDefined(
    order?.emailId,
    order?.customerEmail,
    order?.customer?.email,
    order?.user?.email,
    order?.personalization?.email,
    order?.billingAddress?.email,
    order?.shippingAddress?.email
  );
  const customerPhone = pickDefined(
    order?.mobile,
    order?.phone,
    order?.customerPhone,
    order?.customer?.phone,
    order?.user?.phone,
    order?.personalization?.phone,
    order?.billingAddress?.phone,
    order?.billingAddress?.mobile,
    order?.shippingAddress?.phone,
    order?.shippingAddress?.mobile
  );
  const customerAddress = formatAddress(
    pickDefined(
      order?.address,
      order?.shippingAddress,
      order?.billingAddress,
      order?.customer?.address,
      order?.user?.address
    )
  );
  const personalization = formatPersonalization(
    pickDefined(order?.personalization, order?.metadata?.personalization)
  );
  const customerDetails = [
    {
      label: 'User',
      value: toDisplayText(order?.user?.name || '-')
    },
    {
      label: 'Email',
      value: toDisplayText(customerEmail)
    },
    {
      label: 'Phone',
      value: toDisplayText(customerPhone)
    },
    {
      label: 'Address',
      value: customerAddress
    },
    {
      label: 'Personalization',
      value: personalization
    }
  ];

  useEffect(() => {
    setTrackingNo(currentTrackingNo);
  }, [currentTrackingNo]);

  useEffect(() => {
    setTrackingUrl(currentTrackingUrl);
  }, [currentTrackingUrl]);

  useEffect(() => {
    setSelectedOrderStatus(currentOrderStatus);
  }, [currentOrderStatus]);

  // Fetch or create invoice on component mount or when order changes
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id || !order) return;
      
      setInvoiceLoading(true);
      setInvoiceError('');
      
      try {
        // Try to create invoice from order
        const response = await apiClient.post(`/api/admin/invoices/create-from-order/${id}`);
        if (response.data?.invoice) {
          setInvoice(response.data.invoice);
        }
      } catch (error) {
        // If invoice already exists or other error, try to get existing invoices
        console.log('Could not create invoice:', error.message);
        setInvoiceError(error?.response?.data?.message || '');
      } finally {
        setInvoiceLoading(false);
      }
    };

    if (id && order && !invoice) {
      fetchInvoice();
    }
  }, [id, order, invoice]);

  const handleDownloadInvoice = async () => {
    if (!invoice?._id) return;
    
    try {
      const response = await apiClient.get(`/api/admin/invoices/download/${invoice._id}`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoiceNumber || 'invoice'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setInvoiceError('Failed to download invoice: ' + (error?.response?.data?.message || error.message));
    }
  };

  const onUpdateStatus = async () => {
    if (!id || !effectiveStatus) return;
    setActionError('');
    setActionSuccess('');
    setUpdating(true);
    try {
      await apiClient.patch(`/api/payments/admin/orders/${id}/payment-status`, {
        paymentStatus: effectiveStatus
      });
      setActionSuccess('Payment status updated successfully.');
      await mutate();
    } catch (e) {
      setActionError(e?.response?.data?.message || 'Failed to update payment status.');
    } finally {
      setUpdating(false);
    }
  };

  const onUpdateTracking = async () => {
    const trimmedTrackingNo = String(trackingNo || '').trim();
    const trimmedTrackingUrl = String(trackingUrl || '').trim();
    if (!id || !trimmedTrackingNo || !trimmedTrackingUrl) return;

    setActionError('');
    setActionSuccess('');
    setTrackingUpdating(true);

    try {
      await apiClient.post('/api/admin/dashboard/tracking', {
        orderId: id,
        trackingNo: trimmedTrackingNo,
        trackingUrl: trimmedTrackingUrl,
        orderStatus: 'shipped'
      });
      setSelectedOrderStatus('shipped');
      setActionSuccess('Tracking details updated successfully. Order status set to shipped.');
      await mutate();
    } catch (e) {
      setActionError(e?.response?.data?.message || 'Failed to update tracking details.');
    } finally {
      setTrackingUpdating(false);
    }
  };

  const onUpdateOrderStatus = async () => {
    const fallbackTrackingNo = String(trackingNo || currentTrackingNo || '').trim();
    const fallbackTrackingUrl = String(trackingUrl || currentTrackingUrl || '').trim();
    const nextOrderStatus = String(effectiveOrderStatus || '').trim();
    if (!id || !nextOrderStatus) return;

    setActionError('');
    setActionSuccess('');
    setOrderStatusUpdating(true);

    try {
      await apiClient.post('/api/admin/dashboard/tracking', {
        orderId: id,
        trackingNo: fallbackTrackingNo,
        trackingUrl: fallbackTrackingUrl,
        orderStatus: nextOrderStatus
      });
      setActionSuccess('Order status updated successfully.');
      await mutate();
    } catch (e) {
      setActionError(e?.response?.data?.message || 'Failed to update order status.');
    } finally {
      setOrderStatusUpdating(false);
    }
  };

  if (loading) {
    return <Typography>Loading payment order details...</Typography>;
  }

  if (error) {
    return <Alert severity="error">Failed to load payment order details.</Alert>;
  }

  if (!order) {
    return <Alert severity="warning">Order not found.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Typography variant="h4">Payment Order Details</Typography>
        <Button variant="outlined" onClick={() => navigate('/payments/orders')}>
          Back to Orders
        </Button>
      </Stack>

      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {actionSuccess ? <Alert severity="success">{actionSuccess}</Alert> : null}

      <MainCard>
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                <strong>Order ID:</strong> {order.orderId}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                <strong>Provider:</strong> {order.payment?.provider || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                <strong>Status:</strong>{' '}
                <Chip size="small" label={order.payment?.status || 'unknown'} />
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                <strong>Currency:</strong> {order.currency || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                <strong>Total:</strong> {Number(order.amount?.total || 0).toFixed(2)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                <strong>Tracking No:</strong> {currentTrackingNo || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                <strong>Tracking URL:</strong> {currentTrackingUrl || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                <strong>Order Status:</strong> {currentOrderStatus || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                <strong>Created:</strong> {new Date(order.createdAt).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="h5">Customer Details</Typography>
            <Grid container spacing={1.5}>
              {customerDetails.map((detail) => (
                <Grid
                  key={detail.label}
                  size={{ xs: 12, sm: detail.label === 'Address' || detail.label === 'Personalization' ? 12 : 6, md: detail.label === 'Address' || detail.label === 'Personalization' ? 12 : 4 }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.default'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {detail.label}
                    </Typography>
                    <Typography sx={{ mt: 0.5, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {detail.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Stack>
      </MainCard>

      <MainCard>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1.5}>
              <Typography variant="h5">Update Payment Status</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  select
                  size="small"
                  value={effectiveStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {paymentStatuses.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="contained" disabled={updating} onClick={onUpdateStatus}>
                  {updating ? 'Updating...' : 'Update'}
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1.5}>
              <Typography variant="h5">Update Tracking Details</Typography>
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter tracking number"
                  value={trackingNo}
                  onChange={(e) => setTrackingNo(e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="url"
                  placeholder="Enter tracking URL"
                  value={trackingUrl}
                  onChange={(e) => setTrackingUrl(e.target.value)}
                />
                <Button
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                  variant="contained"
                  disabled={!String(trackingNo || '').trim() || !String(trackingUrl || '').trim() || trackingUpdating}
                  onClick={onUpdateTracking}
                >
                  {trackingUpdating ? 'Updating...' : 'Update'}
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1.5}>
              <Typography variant="h5">Update Order Status</Typography>
              <Stack spacing={1.5}>
                <TextField
                  select
                  size="small"
                  label="Order Status"
                  value={effectiveOrderStatus}
                  onChange={(e) => setSelectedOrderStatus(e.target.value)}
                >
                  {orderStatuses.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                  variant="contained"
                  disabled={!effectiveOrderStatus || orderStatusUpdating}
                  onClick={onUpdateOrderStatus}
                >
                  {orderStatusUpdating ? 'Updating...' : 'Update'}
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </MainCard>

      {invoice && (
        <MainCard>
          <Stack spacing={1.5}>
            <Typography variant="h5">Invoice</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Stack flex={1} spacing={0.5}>
                <Typography>
                  <strong>Invoice Number:</strong> {invoice.invoiceNumber || '-'}
                </Typography>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadInvoice}
              >
                Download Invoice
              </Button>
            </Stack>
          </Stack>
        </MainCard>
      )}

      {invoiceLoading && (
        <MainCard>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={24} />
            <Typography>Generating invoice...</Typography>
          </Stack>
        </MainCard>
      )}

      {invoiceError && !invoice && (
        <Alert severity="warning">{invoiceError}</Alert>
      )}

      <MainCard title="Order Items">
        <Stack spacing={1.5}>
          {(order.items || []).map((item, index) => {
            const product =
              item?.product && typeof item.product === 'object' ? item.product : {};
            const variant =
              item?.productVariant && typeof item.productVariant === 'object'
                ? item.productVariant
                : null;
            const title = item?.title || variant?.title || product?.title || 'Item';
            const quantity = item?.quantity ?? 1;
            const isCustomizedProduct = Boolean(
              item?.isCustomize ?? variant?.isCustomize ?? product?.isCustomize
            );
            const sku =
              item?.sku ||
              item?.SKU ||
              variant?.sku ||
              variant?.SKU ||
              product?.sku ||
              product?.SKU ||
              '-';
            const selectedAttributes =
              item?.selectedAttributes || variant?.selectedAttributes || {};
            const attributesLabel = buildAttributesLabel(selectedAttributes, [
              'goldPurity',
              'metalType'
            ]);
            const purityCandidates = pickDefined(
              item?.selectedAttributes?.goldPurity,
              variant?.goldPurity,
              variant?.metal?.gold?.purity,
              product?.gold_purity,
              product?.goldPurity,
              product?.metal?.gold?.purity
            );
            const purityList = normalizeList(purityCandidates)
              .map(normalizePurity)
              .filter(Boolean);
            const purityLabel = purityList.length ? purityList.join(', ') : '-';
            const goldWeight = getGoldWeight({
              variant,
              product,
              purity: purityList[0]
            });
            const platinumWeight = getPlatinumWeight({ variant, product, item });
            const diamondWeight = getDiamondWeight({ variant, product, item });
            const gemstoneWeight = getGemstoneWeight({ variant, product, item });
            const hasGoldPurity = purityList.length > 0;
            const metalTypes = [
              ...normalizeMetalTypes(variant?.metalType),
              ...normalizeMetalTypes(variant?.metal?.type),
              ...normalizeMetalTypes(item?.metalType),
              ...normalizeMetalTypes(item?.selectedAttributes?.metalType),
              ...normalizeMetalTypes(product?.metal?.type)
            ];
            let uniqueMetalTypes = [
              ...new Set(
                metalTypes
                  .map((type) => String(type || '').trim())
                  .filter(Boolean)
                  .map((type) => type.charAt(0).toUpperCase() + type.slice(1).toLowerCase())
              )
            ];
            if (hasGoldPurity) {
              uniqueMetalTypes = ['Gold'];
            } else if (!uniqueMetalTypes.length) {
              if (product?.metal?.platinum || platinumWeight !== null) {
                uniqueMetalTypes = ['Platinum'];
              } else if (product?.metal?.gold || goldWeight !== null) {
                uniqueMetalTypes = ['Gold'];
              }
            }
            const metalLabel = uniqueMetalTypes.length ? uniqueMetalTypes.join(', ') : '-';
            const hasGold = uniqueMetalTypes.some((type) => type.toLowerCase() === 'gold');
            const hasPlatinum = uniqueMetalTypes.some(
              (type) => type.toLowerCase() === 'platinum'
            );
            const hasGoldWeight = Number(goldWeight) > 0;
            const hasPlatinumWeight = Number(platinumWeight) > 0;
            const hasDiamondWeight = Number(diamondWeight) > 0;
            const hasGemstoneWeight = Number(gemstoneWeight) > 0;
            const image = resolveImage(item, variant, product);
            const unitPrice = Number(item?.unitPrice || 0).toFixed(2);
            const subtotal = Number(item?.subtotal || 0).toFixed(2);

            return (
              <Stack
                key={`${item?.product?._id || item?.productVariant?._id || sku}-${index}`}
                spacing={1.2}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  {image ? (
                    <Box
                      component="img"
                      src={image}
                      alt={title}
                      sx={{
                        width: 88,
                        height: 88,
                        borderRadius: 1,
                        objectFit: 'cover',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 88,
                        height: 88,
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        fontSize: 12
                      }}
                    >
                      No image
                    </Box>
                  )}
                  <Stack spacing={0.4} flex={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {title} x {quantity}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unit: {unitPrice} | Subtotal: {subtotal}
                    </Typography>
                    {!isCustomizedProduct && (
                      <>
                        <Typography variant="body2">
                          <strong>SKU:</strong> {toDisplayText(sku)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Metal:</strong> {metalLabel}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Gold Purity:</strong> {purityLabel}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Attributes:</strong> {attributesLabel}
                        </Typography>
                      </>
                    )}
                    {hasGold && hasGoldWeight && (
                      <Typography variant="body2">
                        <strong>Gold Weight:</strong> {formatWeight(goldWeight)}
                      </Typography>
                    )}
                    {hasPlatinum && hasPlatinumWeight && (
                      <Typography variant="body2">
                        <strong>Platinum Weight:</strong> {formatWeight(platinumWeight)}
                      </Typography>
                    )}
                    {hasDiamondWeight && (
                      <Typography variant="body2">
                        <strong>Diamond Weight:</strong> {formatWeight(diamondWeight)}
                      </Typography>
                    )}
                    {hasGemstoneWeight && (
                      <Typography variant="body2">
                        <strong>Gemstone Weight:</strong> {formatWeight(gemstoneWeight)}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                {index < (order.items || []).length - 1 ? <Divider /> : null}
              </Stack>
            );
          })}
          {!order.items?.length && <Typography color="text.secondary">No items found.</Typography>}
        </Stack>
      </MainCard>
    </Stack>
  );
}
